import moment from 'moment';
import { AuditEvent } from '../../components/services/auditService';
import { ActivityLogScope } from './activityLogFilters';
import { actorDisplayName, describeAuditValue, describeChange, describeTarget, formatAuditTimePrecise } from './auditFormat';

export interface ActivityLogCsvOptions {
  /** Action code -> customer-facing name (GET auditlog/actions). */
  actionNames: Record<string, string>;
  /** tenantId (lowercase) -> name; the Tenant column is only added for the cross-tenant view. */
  tenantNames: Record<string, string>;
  showTenantColumn: boolean;
  /** Globaladmin: Target counts the other changes and a Changes column lists them. Others: the main record only. */
  fullDetail: boolean;
}

// Plain ASCII in the CSV: the shared downloadCSV writes UTF-8 without a BOM, which Excel would misread for "→" or "—".
// An id field shows the record's name with the id: "LAPTOP-01 (00227bee-...)".
const valueText = (value: unknown, name?: string | null) => {
  if (value === undefined || value === null) return '(empty)';
  const text = describeAuditValue(value).text;
  return name ? `${name} (${text})` : text;
};

/**
 * One change as text: `Label "name" [key] (Operation)` (or `Label key (Operation)` without a name), plus
 * "field: old -> new" for each field the caller may see.
 */
const changeText = (change: AuditEvent['changes'][number]): string => {
  const keySuffix = change.displayName && change.key ? ` [${change.key}]` : '';
  const head = `${describeChange(change, 'full')}${keySuffix} (${change.operation})`;
  const fields = (change.fields ?? []).map(
    (field) => `${field.name}: ${valueText(field.old, field.oldName)} -> ${valueText(field.new, field.newName)}`,
  );
  return fields.length ? `${head}: ${fields.join('; ')}` : head;
};

const tenantLabel = (tenantKey: string, tenantNames: Record<string, string>) =>
  tenantKey === 'global' ? 'Global' : tenantNames[tenantKey] || tenantKey;

/**
 * The CSV columns (downloadCSV's key -> header map, in order) and one row per event, from what the AuditAPI returned.
 * Field values, IP, correlation id and failure reason are only filled for Globaladmin; the API leaves them empty for
 * everyone else, so their columns stay blank. The Changes column is Globaladmin only (`fullDetail`).
 */
export const buildActivityLogCsv = (
  events: AuditEvent[],
  { actionNames, tenantNames, showTenantColumn, fullDetail }: ActivityLogCsvOptions,
) => {
  const columns: Record<string, string> = {
    time: 'Time',
    timestampUtc: 'Time (UTC)',
    user: 'User',
    email: 'Email',
    action: 'Action',
    actionCode: 'Action code',
    category: 'Category',
    outcome: 'Outcome',
    target: 'Target',
    ...(showTenantColumn ? { tenant: 'Tenant' } : {}),
    service: 'Service',
    ...(fullDetail ? { changes: 'Changes' } : {}),
    failureReason: 'Failure reason',
    ip: 'IP address',
    correlationId: 'Correlation ID',
    eventId: 'Event ID',
  };

  const rows = events.map((event) => ({
    time: formatAuditTimePrecise(event.timestampUtc),
    timestampUtc: event.timestampUtc,
    user: actorDisplayName(event.actor),
    email: event.actor?.email ?? '',
    // Same rule as useAuditActions' actionDisplayName (not imported: that module pulls in the api client).
    action: actionNames[event.action] || event.action,
    actionCode: event.action,
    category: event.category,
    outcome: event.outcome,
    target: describeTarget(event, { countOthers: fullDetail }).replace(/^—$/, ''),
    tenant: tenantLabel(event.tenantKey, tenantNames),
    service: event.service,
    changes: (event.changes ?? []).map(changeText).join(' | '),
    failureReason: event.failureReason ?? '',
    ip: event.actor?.ip ?? '',
    correlationId: event.correlationId ?? '',
    eventId: event.eventId,
  }));

  return { columns, rows };
};

/** e.g. activity_log_tenant_2026-10-19 (downloadCSV adds .csv). */
export const activityLogCsvFileName = (scope: ActivityLogScope, now: moment.Moment = moment()) =>
  `activity_log_${scope === 'all' ? 'all_tenants' : scope}_${now.format('YYYY-MM-DD')}`;
